interface CircuitBreakerConfig {
    failureThreshold: number;
    recoveryTimeout: number;
    monitoringPeriod: number;
}

interface CircuitBreakerState {
    failures: number;
    lastFailureTime: number;
    state: 'CLOSED' | 'OPEN' | 'HALF_OPEN';
    nextAttempt: number;
}

class CircuitBreaker {
    private config: CircuitBreakerConfig;
    private state: CircuitBreakerState;
    private monitoringTimer: NodeJS.Timeout | null = null;

    constructor(config: Partial<CircuitBreakerConfig> = {}) {
        this.config = {
            failureThreshold: 5,
            recoveryTimeout: 60000, // 1 minute
            monitoringPeriod: 300000, // 5 minutes
            ...config
        };

        this.state = {
            failures: 0,
            lastFailureTime: 0,
            state: 'CLOSED',
            nextAttempt: 0
        };

        this.startMonitoring();
    }

    async execute<T>(fn: () => Promise<T>, fallback?: () => Promise<T>): Promise<T> {
        if (this.state.state === 'OPEN') {
            if (Date.now() < this.state.nextAttempt) {
                console.log('🔴 Circuit breaker is OPEN, using fallback');
                if (fallback) {
                    return await fallback();
                }
                throw new Error('Circuit breaker is OPEN and no fallback provided');
            } else {
                this.state.state = 'HALF_OPEN';
                console.log('🟡 Circuit breaker moved to HALF_OPEN');
            }
        }

        try {
            const result = await fn();
            this.onSuccess();
            return result;
        } catch (error) {
            this.onFailure();
            console.error('❌ Circuit breaker caught error:', error);
            
            if (fallback) {
                console.log('🔄 Using fallback function');
                return await fallback();
            }
            
            throw error;
        }
    }

    private onSuccess(): void {
        this.state.failures = 0;
        if (this.state.state === 'HALF_OPEN') {
            this.state.state = 'CLOSED';
            console.log('🟢 Circuit breaker moved to CLOSED');
        }
    }

    private onFailure(): void {
        this.state.failures++;
        this.state.lastFailureTime = Date.now();

        if (this.state.failures >= this.config.failureThreshold) {
            this.state.state = 'OPEN';
            this.state.nextAttempt = Date.now() + this.config.recoveryTimeout;
            console.log(`🔴 Circuit breaker moved to OPEN (${this.state.failures} failures)`);
        }
    }

    private startMonitoring(): void {
        this.monitoringTimer = setInterval(() => {
            this.monitor();
        }, this.config.monitoringPeriod);
    }

    private monitor(): void {
        const now = Date.now();
        const timeSinceLastFailure = now - this.state.lastFailureTime;
        
        // Reset failures if enough time has passed
        if (timeSinceLastFailure > this.config.monitoringPeriod && this.state.failures > 0) {
            console.log('🔄 Circuit breaker: resetting failure count due to monitoring period');
            this.state.failures = Math.max(0, this.state.failures - 1);
        }

        console.log(`📊 Circuit breaker state: ${this.state.state}, failures: ${this.state.failures}`);
    }

    getState(): CircuitBreakerState {
        return { ...this.state };
    }

    reset(): void {
        this.state = {
            failures: 0,
            lastFailureTime: 0,
            state: 'CLOSED',
            nextAttempt: 0
        };
        console.log('🔄 Circuit breaker reset');
    }

    destroy(): void {
        if (this.monitoringTimer) {
            clearInterval(this.monitoringTimer);
            this.monitoringTimer = null;
        }
    }
}

// Global circuit breaker instances
export const apiCircuitBreaker = new CircuitBreaker({
    failureThreshold: 3,
    recoveryTimeout: 30000, // 30 seconds
    monitoringPeriod: 120000 // 2 minutes
});

export const taxiStandsCircuitBreaker = new CircuitBreaker({
    failureThreshold: 5,
    recoveryTimeout: 60000, // 1 minute
    monitoringPeriod: 300000 // 5 minutes
});

export default CircuitBreaker;
